import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  Devices as DevicesIcon,
  Telegram as TelegramIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
// menuUtils removed Session 3 cutover; this page no longer needs menuStructure.
import EntityHistoryButton from '../components/common/EntityHistoryButton';
import { UserLoginHistoryButton } from '../components/common/UserLoginHistory';
import regularUserService, { RegularUser, UserDevicesResponse } from '../services/regularUserService';
import UserPlatformBadge, { PLATFORM_LABEL_FA } from '../components/common/UserPlatformBadge';
import UserDevicesDialog from '../components/common/UserDevicesDialog';
import { adminKycService } from '../services/kyc.service';
import apiService from '../services/api';
import './EntityManagement.css';
import './RegularUserManagement.css';

const RegularUserManagement: React.FC = () => {
  // menuStructure removed; useAuth retained for potential user-id needs below.
  useAuth();

  // Permission checks
  const canViewUsers = true;
  const canBanUser = true;
  const canUnbanUser = true;

  // State
  const [users, setUsers] = useState<RegularUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState<'name' | 'email'>('name');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'banned'>('all');
  /** فیلتر کانال: از اپ می‌آید، از سایت، هر دو، یا هنوز نامشخص */
  const [filterChannel, setFilterChannel] = useState<'all' | 'app' | 'web' | 'both' | 'telegram' | 'unknown'>('all');
  const [sortBy, setSortBy] = useState<'createdAt' | 'lastActive'>('createdAt');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // دستگاه‌های کاربر (مودال)
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [devicesUser, setDevicesUser] = useState<RegularUser | null>(null);
  const [devicesData, setDevicesData] = useState<UserDevicesResponse | null>(null);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsUser, setDetailsUser] = useState<RegularUser | null>(null);
  const [banOpen, setBanOpen] = useState(false);
  const [banUser, setBanUser] = useState<RegularUser | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banError, setBanError] = useState('');
  const [unbanOpen, setUnbanOpen] = useState(false);
  const [unbanUser, setUnbanUser] = useState<RegularUser | null>(null);
  const [rejectKycLoading, setRejectKycLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' as 'success' | 'error' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchUsers(); }, [page, limit, filterStatus, filterChannel, sortBy]);

  const getId = (u: RegularUser | null) => u?.id || u?._id || '';
  const formatDate = (d: string | undefined) => d ? new Date(d).toLocaleDateString('fa-IR') : '-';
  const formatDateTime = (d: string | undefined) => {
    if (!d) return '-';
    const date = new Date(d);
    return `${date.toLocaleDateString('fa-IR')} - ${date.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}`;
  };
  const notify = (msg: string, type: 'success' | 'error') => setSnackbar({ open: true, msg, type });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let isActive: boolean | undefined = undefined;
      let isBanned: boolean | undefined = undefined;

      if (filterStatus === 'active') {
        isActive = true;
        isBanned = false;
      } else if (filterStatus === 'banned') {
        isBanned = true;
      }

      const response = await regularUserService.getRegularUsers(
        page + 1,
        limit,
        search || undefined,
        isActive,
        isBanned,
        { channel: filterChannel, sort: sortBy }
      );

      let data: RegularUser[] = [];
      if (response && response.data) {
        data = Array.isArray(response.data) ? response.data : [];
        setTotal(response.pagination?.total || data.length);
      }

      // Client-side filtering based on search type
      if (search.trim()) {
        const term = search.toLowerCase();
        if (searchType === 'name') {
          data = data.filter(u => u.name.toLowerCase().includes(term));
        } else if (searchType === 'email') {
          data = data.filter(u => u.email.toLowerCase().includes(term));
        }
      }

      setUsers(data);
    } catch {
      notify('خطا در دریافت لیست کاربران', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  /** مودالِ دستگاه‌ها — داده جدا گرفته می‌شود چون سنگین است و اکثر ردیف‌ها باز نمی‌شوند. */
  const openDevices = async (user: RegularUser) => {
    setDevicesUser(user);
    setDevicesOpen(true);
    setDevicesData(null);
    setDevicesLoading(true);
    try {
      const data = await regularUserService.getUserDevices(getId(user));
      setDevicesData(data);
    } catch {
      notify('خطا در دریافت دستگاه‌های کاربر', 'error');
      setDevicesOpen(false);
    } finally {
      setDevicesLoading(false);
    }
  };

  const openDetails = async (user: RegularUser) => {
    try {
      setLoading(true);
      const userDetails = await regularUserService.getRegularUserDetails(getId(user));

      let enrichedUserDetails = { ...userDetails };

      // Fetch ban admin info
      if (typeof userDetails.bannedBy === 'string' && userDetails.bannedBy) {
        try {
          const adminResponse = await apiService.get(`/admin/users/${userDetails.bannedBy}`);
          if (adminResponse) {
            enrichedUserDetails.bannedBy = (adminResponse as any).data || adminResponse;
          }
        } catch {}
      }

      // Fetch unban admin info
      if (typeof userDetails.unbannedBy === 'string' && userDetails.unbannedBy) {
        try {
          const adminResponse = await apiService.get(`/admin/users/${userDetails.unbannedBy}`);
          if (adminResponse) {
            enrichedUserDetails.unbannedBy = (adminResponse as any).data || adminResponse;
          }
        } catch {}
      }

      setDetailsUser(enrichedUserDetails);
      setDetailsOpen(true);
    } catch {
      notify('خطا در دریافت اطلاعات کاربر', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBan = async () => {
    if (!banUser) return;

    if (!banReason.trim()) {
      setBanError('لطفاً دلیل مسدود کردن کاربر را وارد کنید');
      return;
    }

    try {
      setLoading(true);
      await regularUserService.banUser(getId(banUser), banReason);
      notify('کاربر با موفقیت مسدود شد', 'success');
      setBanOpen(false);
      setBanUser(null);
      setBanReason('');
      setBanError('');
      fetchUsers();
    } catch {
      notify('خطا در مسدود کردن کاربر', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnban = async () => {
    if (!unbanUser) return;

    try {
      setLoading(true);
      await regularUserService.unbanUser(getId(unbanUser));
      notify('رفع مسدودیت کاربر با موفقیت انجام شد', 'success');
      setUnbanOpen(false);
      setUnbanUser(null);
      fetchUsers();
    } catch {
      notify('خطا در رفع مسدودیت کاربر', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getAdminName = (admin: any): string => {
    if (!admin) return '-';
    if (typeof admin === 'string') return admin;
    if (admin.fullName) return admin.fullName;
    if (admin.name) return admin.name;
    const firstName = admin.firstName || '';
    const lastName = admin.lastName || '';
    if (firstName || lastName) return `${firstName} ${lastName}`.trim();
    return admin.email || admin.id || '-';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="regular-users-page">
      {/* Header */}
      <header className="users-header">
        <div>
          <h1 className="users-title">مدیریت کاربران</h1>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>مدیریت کاربران عادی سایت</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchUsers} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </header>

      {/* Stats */}
      <div className="users-stats">
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon blue"><PeopleIcon /></div>
              <div>
                <p className="users-stat-label">کل کاربران</p>
                <p className="users-stat-value">{total}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon green"><CheckIcon /></div>
              <div>
                <p className="users-stat-label">فعال</p>
                <p className="users-stat-value">{users.filter(u => u.isActive && !u.isBanned).length}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-card-inner">
            <div className="users-stat-card-content">
              <div className="users-stat-icon red"><BlockIcon /></div>
              <div>
                <p className="users-stat-label">مسدود</p>
                <p className="users-stat-value">{users.filter(u => u.isBanned).length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو کاربران..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchUsers()}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value as 'all' | 'active' | 'banned'); setPage(0); }}
            >
              <option value="all">همه وضعیت‌ها</option>
              <option value="active">فعال</option>
              <option value="banned">مسدود شده</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">کانال استفاده</label>
            <select
              className="filter-field"
              value={filterChannel}
              onChange={e => { setFilterChannel(e.target.value as typeof filterChannel); setPage(0); }}
            >
              <option value="all">همه کانال‌ها</option>
              <option value="app">فقط کاربران اپ</option>
              <option value="web">فقط کاربران وب</option>
              <option value="both">هر دو (اپ و وب)</option>
              <option value="telegram">فقط از تلگرام</option>
              <option value="unknown">بدون فعالیت ثبت‌شده</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">مرتب‌سازی</label>
            <select
              className="filter-field"
              value={sortBy}
              onChange={e => { setSortBy(e.target.value as typeof sortBy); setPage(0); }}
            >
              <option value="createdAt">تاریخ عضویت</option>
              <option value="lastActive">آخرین فعالیت</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={() => fetchUsers()}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {(search || filterStatus !== 'all') && (
              <button className="filter-btn outline" onClick={() => { setSearch(''); setFilterStatus('all'); setFilterChannel('all'); setPage(0); fetchUsers(); }}>
                پاک
              </button>
            )}
          </div>
        </div>
        {(search || filterStatus !== 'all' || filterChannel !== 'all') && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {search && (
              <span className="filter-tag">
                {searchType === 'name' ? 'نام' : 'ایمیل'}: {search}
                <button onClick={() => { setSearch(''); fetchUsers(); }}>×</button>
              </span>
            )}
            {filterStatus !== 'all' && (
              <span className="filter-tag">
                وضعیت: {filterStatus === 'active' ? 'فعال' : 'مسدود'}
                <button onClick={() => { setFilterStatus('all'); fetchUsers(); }}>×</button>
              </span>
            )}
            {filterChannel !== 'all' && (
              <span className="filter-tag">
                کانال: {
                  filterChannel === 'app' ? 'اپلیکیشن'
                    : filterChannel === 'web' ? 'وب‌سایت'
                    : filterChannel === 'both' ? 'اپ و وب'
                    : filterChannel === 'telegram' ? 'تلگرام'
                    : 'بدون فعالیت'
                }
                <button onClick={() => { setFilterChannel('all'); }}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={() => { setSearch(''); setFilterStatus('all'); setFilterChannel('all'); setPage(0); fetchUsers(); }}>
              پاک کردن همه
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="users-table-wrapper">
        {loading && users.length === 0 ? (
          <div className="users-loading"><CircularProgress size={36} /></div>
        ) : users.length === 0 ? (
          <div className="users-empty">
            <PeopleIcon className="users-empty-icon" />
            <p className="users-empty-text">کاربری یافت نشد</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="users-table">
              <thead>
                <tr>
                  <th>کاربر</th>
                  <th>وضعیت</th>
                  <th>پلتفرم</th>
                  <th>آخرین فعالیت</th>
                  <th>تاریخ عضویت</th>
                  <th>آخرین ورود</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={getId(user)} className={user.isBanned ? 'row-banned' : ''}>
                    <td>
                      <div className="user-info-cell">
                        <div className="user-avatar" onClick={() => openDetails(user)}>
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.name} />
                          ) : (
                            <PersonIcon />
                          )}
                        </div>
                        <div className="user-info">
                          <div className="user-name">{user.name}</div>
                          <div className="user-email">{user.email}</div>
                          {user.isBanned && (
                            <div className="user-badges">
                              <span className="tag tag-xs tag-danger">مسدود</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {user.isBanned ? (
                        <span className="tag tag-sm tag-banned">مسدود شده</span>
                      ) : user.isActive ? (
                        <span className="tag tag-sm tag-active">فعال</span>
                      ) : (
                        <span className="tag tag-sm tag-inactive">غیرفعال</span>
                      )}
                    </td>
                    <td>
                      <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => openDevices(user)}
                        title="مشاهدهٔ دستگاه‌ها و سشن‌ها"
                      >
                        <UserPlatformBadge usage={user.usage} />
                      </div>
                    </td>
                    <td className="cell-date">{formatDateTime(user.usage?.lastActiveAt)}</td>
                    <td className="cell-date">{formatDate(user.createdAt)}</td>
                    <td className="cell-date">{formatDateTime(user.lastLogin)}</td>
                    <td>
                      <div className="actions-cell">
                        <button className="action-btn view" onClick={() => openDetails(user)} title="جزئیات">
                          <ViewIcon fontSize="small" />
                        </button>
                        <button className="action-btn view" onClick={() => openDevices(user)} title="دستگاه‌ها و فعالیت">
                          <DevicesIcon fontSize="small" />
                        </button>
                        <UserLoginHistoryButton userId={getId(user)} userName={user.name} buttonLabel="" size="small" />
                        <EntityHistoryButton entityType="User" entityId={getId(user)} buttonLabel="" size="small" />
                        {canBanUser && !user.isBanned && (
                          <button className="action-btn ban" onClick={() => { setBanUser(user); setBanOpen(true); }} title="مسدود کردن">
                            <BlockIcon fontSize="small" />
                          </button>
                        )}
                        {canUnbanUser && user.isBanned && (
                          <button className="action-btn unban" onClick={() => { setUnbanUser(user); setUnbanOpen(true); }} title="رفع مسدودیت">
                            <CheckIcon fontSize="small" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="users-cards">
              {users.map(user => {
                const isOpen = expandedId === getId(user);
                return (
                  <div key={getId(user)} className={`user-card ${user.isBanned ? 'card-banned' : ''}`}>
                    <div className="user-card-header" onClick={() => setExpandedId(isOpen ? null : getId(user))}>
                      <div className="user-card-avatar" onClick={e => { e.stopPropagation(); openDetails(user); }}>
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} />
                        ) : (
                          <PersonIcon />
                        )}
                      </div>
                      <div className="user-card-info">
                        <div className="user-card-name">{user.name}</div>
                        <div className="user-card-email">{user.email}</div>
                        {user.isBanned && (
                          <div className="user-card-badges">
                            <span className="tag tag-xs tag-danger">مسدود</span>
                          </div>
                        )}
                      </div>
                      <div className="user-card-status">
                        {user.isBanned ? (
                          <span className="tag tag-sm tag-banned">مسدود</span>
                        ) : user.isActive ? (
                          <span className="tag tag-sm tag-active">فعال</span>
                        ) : (
                          <span className="tag tag-sm tag-inactive">غیرفعال</span>
                        )}
                        <button className={`user-card-toggle ${isOpen ? 'open' : ''}`}>
                          <KeyboardArrowDown fontSize="small" />
                        </button>
                      </div>
                    </div>

                    <div className={`user-card-details ${isOpen ? 'open' : ''}`}>
                      <div className="user-card-row">
                        <span className="user-card-label">پلتفرم</span>
                        <span className="user-card-value">
                          <UserPlatformBadge usage={user.usage} showDetail={false} />
                        </span>
                      </div>
                      <div className="user-card-row">
                        <span className="user-card-label">آخرین فعالیت</span>
                        <span className="user-card-value ltr">{formatDateTime(user.usage?.lastActiveAt)}</span>
                      </div>
                      <div className="user-card-row">
                        <span className="user-card-label">تاریخ عضویت</span>
                        <span className="user-card-value">{formatDate(user.createdAt)}</span>
                      </div>
                      <div className="user-card-row">
                        <span className="user-card-label">آخرین ورود</span>
                        <span className="user-card-value ltr">{formatDateTime(user.lastLogin)}</span>
                      </div>
                      {user.isBanned && user.banReason && (
                        <div className="user-card-row">
                          <span className="user-card-label">دلیل مسدودی</span>
                          <span className="user-card-value">{user.banReason}</span>
                        </div>
                      )}
                      <div className="user-card-actions">
                        <button className="user-card-btn" onClick={() => openDetails(user)}>
                          <ViewIcon fontSize="small" /> جزئیات
                        </button>
                        <UserLoginHistoryButton userId={getId(user)} userName={user.name} buttonLabel="ورودها" size="small" variant="outlined" />
                        <EntityHistoryButton entityType="User" entityId={getId(user)} buttonLabel="تاریخچه" size="small" />
                        {canBanUser && !user.isBanned && (
                          <button className="user-card-btn danger" onClick={() => { setBanUser(user); setBanOpen(true); }}>
                            <BlockIcon fontSize="small" /> مسدود
                          </button>
                        )}
                        {canUnbanUser && user.isBanned && (
                          <button className="user-card-btn success" onClick={() => { setUnbanUser(user); setUnbanOpen(true); }}>
                            <CheckIcon fontSize="small" /> رفع مسدودیت
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="users-pagination">
              <span className="pagination-info">{page * limit + 1} تا {Math.min((page + 1) * limit, total)} از {total}</span>
              <div className="pagination-controls">
                <select className="users-select" value={limit} onChange={e => { setLimit(+e.target.value); setPage(0); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
                  <ChevronLeft fontSize="small" />
                </button>
                <button className="pagination-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                  <ChevronRight fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* User Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth className="details-dialog">
        <DialogTitle className="details-dialog-title">
          <span>جزئیات کاربر</span>
          <button className="details-close" onClick={() => setDetailsOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </DialogTitle>
        <DialogContent className="details-dialog-content">
          {detailsUser && (
            <div className="details-content">
              <div className="details-header">
                <div className="details-avatar">
                  {detailsUser.avatar ? (
                    <img src={detailsUser.avatar} alt={detailsUser.name} />
                  ) : (
                    <PersonIcon />
                  )}
                </div>
                <div className="details-title">
                  <h3>{detailsUser.name}</h3>
                  <span className="details-email">{detailsUser.email}</span>
                  <div className="details-tags">
                    {detailsUser.isBanned ? (
                      <span className="tag tag-sm tag-banned">مسدود شده</span>
                    ) : detailsUser.isActive ? (
                      <span className="tag tag-sm tag-active">فعال</span>
                    ) : (
                      <span className="tag tag-sm tag-inactive">غیرفعال</span>
                    )}
                  </div>
                </div>
              </div>

              {/*
                هویت تلگرامی — برای حساب‌های مهمانِ ربات، تنها اطلاعاتی است که
                وجود دارد (نه شماره‌ای هست نه ایمیلی). بدون این، دیالوگ جزئیات
                برای این کاربران عملاً خالی بود.
              */}
              {detailsUser.telegram && (
                <div className="tg-info-card">
                  <div className="tg-info-title">
                    <TelegramIcon fontSize="small" />
                    حساب تلگرام
                    {detailsUser.telegram.isGuest && (
                      <span className="tag tag-xs tag-warning">مهمان — شماره وصل نکرده</span>
                    )}
                  </div>

                  <div className="ban-info-row">
                    <span className="ban-info-label">نام در تلگرام:</span>
                    <span className="ban-info-value">{detailsUser.telegram.firstName || '—'}</span>
                  </div>
                  <div className="ban-info-row">
                    <span className="ban-info-label">یوزرنیم:</span>
                    <span className="ban-info-value ltr">
                      {detailsUser.telegram.username ? (
                        <a
                          href={`https://t.me/${detailsUser.telegram.username}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          @{detailsUser.telegram.username}
                        </a>
                      ) : (
                        '— (یوزرنیم ندارد)'
                      )}
                    </span>
                  </div>
                  <div className="ban-info-row">
                    <span className="ban-info-label">شناسهٔ تلگرام:</span>
                    <span className="ban-info-value ltr">{detailsUser.telegram.telegramUserId}</span>
                  </div>
                  <div className="ban-info-row">
                    <span className="ban-info-label">اولین ارتباط:</span>
                    <span className="ban-info-value">
                      {formatDateTime(detailsUser.telegram.linkedAt || undefined)}
                    </span>
                  </div>

                  {!!detailsUser.telegram.channels?.length && (
                    <div className="ban-info-row">
                      <span className="ban-info-label">کانال‌ها:</span>
                      <span className="ban-info-value">
                        {detailsUser.telegram.channels.map((c) => (
                          <div key={c.chatId}>
                            {c.title || c.chatId}
                            {' — '}
                            {c.status === 'member' ? 'عضو' : 'خارج شده'}
                            {c.viaLink && c.linkCode ? ` · با لینک ${c.linkCode}` : ''}
                          </div>
                        ))}
                      </span>
                    </div>
                  )}

                  {!!detailsUser.telegram.chats?.length && (
                    <div className="ban-info-row">
                      <span className="ban-info-label">گفتگوها:</span>
                      <span className="ban-info-value">
                        {detailsUser.telegram.chats.map((c) => (
                          <div key={c.id}>{c.title}</div>
                        ))}
                      </span>
                    </div>
                  )}

                  {detailsUser.telegram.isGuest && (
                    <div className="tg-info-note">
                      این حساب را خودِ ربات ساخته تا کاربر بتواند بدون ثبت‌نام دربارهٔ محصول
                      گفتگو کند. شماره موبایل ندارد و نمی‌تواند وارد سایت شود؛ اگر بعداً
                      شماره‌اش را وصل کند، گفتگوهایش به حساب واقعی منتقل می‌شود.
                    </div>
                  )}
                </div>
              )}

              {/* Ban Info */}
              {detailsUser.isBanned && detailsUser.banReason && (
                <div className="ban-info-card">
                  <div className="ban-info-title">
                    <BlockIcon fontSize="small" />
                    اطلاعات مسدودیت
                  </div>
                  <div className="ban-info-row">
                    <span className="ban-info-label">دلیل مسدودیت:</span>
                    <span className="ban-info-value">{detailsUser.banReason}</span>
                  </div>
                  <div className="ban-info-row">
                    <span className="ban-info-label">مسدود شده توسط:</span>
                    <span className="ban-info-value">{getAdminName(detailsUser.bannedBy)}</span>
                  </div>
                  <div className="ban-info-row">
                    <span className="ban-info-label">تاریخ مسدودیت:</span>
                    <span className="ban-info-value">{formatDateTime(detailsUser.bannedAt as string)}</span>
                  </div>
                </div>
              )}

              {/* Unban Info */}
              {!detailsUser.isBanned && detailsUser.unbannedAt && (
                <div className="unban-info-card">
                  <div className="unban-info-title">
                    <CheckIcon fontSize="small" />
                    اطلاعات رفع مسدودیت
                  </div>
                  <div className="ban-info-row">
                    <span className="ban-info-label">رفع مسدودیت توسط:</span>
                    <span className="ban-info-value">{getAdminName(detailsUser.unbannedBy)}</span>
                  </div>
                  <div className="ban-info-row">
                    <span className="ban-info-label">تاریخ رفع مسدودیت:</span>
                    <span className="ban-info-value">{formatDateTime(detailsUser.unbannedAt as string)}</span>
                  </div>
                  {detailsUser.banReason && (
                    <div className="ban-info-row">
                      <span className="ban-info-label">دلیل مسدودیت قبلی:</span>
                      <span className="ban-info-value">{detailsUser.banReason}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="details-grid">
                <div className="details-item">
                  <span className="details-label">تاریخ عضویت</span>
                  <span className="details-value">{formatDate(detailsUser.createdAt)}</span>
                </div>
                <div className="details-item">
                  <span className="details-label">آخرین ورود</span>
                  <span className="details-value">{formatDateTime(detailsUser.lastLogin)}</span>
                </div>
              </div>

              <div className="details-actions">
                <UserLoginHistoryButton userId={getId(detailsUser)} userName={detailsUser.name} buttonLabel="تاریخچه ورود" size="small" variant="outlined" />
                <EntityHistoryButton entityType="User" entityId={getId(detailsUser)} buttonLabel="تاریخچه تغییرات" size="small" />
                {canBanUser && !detailsUser.isBanned && (
                  <button className="btn btn-danger" onClick={() => { setDetailsOpen(false); setBanUser(detailsUser); setBanOpen(true); }}>
                    <BlockIcon fontSize="small" /> مسدود کردن
                  </button>
                )}
                {canUnbanUser && detailsUser.isBanned && (
                  <button className="btn btn-success" onClick={() => { setDetailsOpen(false); setUnbanUser(detailsUser); setUnbanOpen(true); }}>
                    <CheckIcon fontSize="small" /> رفع مسدودیت
                  </button>
                )}
                <button
                  className="btn btn-ghost"
                  disabled={rejectKycLoading}
                  onClick={async () => {
                    const reason = prompt('دلیل رد KYC کاربر را وارد کنید:') || '';
                    try {
                      setRejectKycLoading(true);
                      await adminKycService.rejectLatestByUser(getId(detailsUser), reason);
                      notify('KYC کاربر رد شد. کاربر می‌تواند دوباره ارسال کند.', 'success');
                    } catch {
                      notify('خطا در رد KYC کاربر', 'error');
                    } finally {
                      setRejectKycLoading(false);
                    }
                  }}
                >
                  رد KYC
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* دستگاه‌ها و سشن‌های کاربر */}
      <UserDevicesDialog
        open={devicesOpen}
        onClose={() => { setDevicesOpen(false); setDevicesUser(null); setDevicesData(null); }}
        userName={devicesUser?.name}
        loading={devicesLoading}
        data={devicesData}
      />

      {/* Ban Dialog */}
      <Dialog open={banOpen} onClose={() => { setBanOpen(false); setBanUser(null); setBanReason(''); setBanError(''); }} maxWidth="sm" fullWidth className="ban-dialog">
        <DialogTitle>مسدود کردن کاربر</DialogTitle>
        <DialogContent className="ban-dialog-content">
          {banUser && (
            <>
              <div className="ban-user-info">
                <div className="ban-user-avatar">
                  {banUser.avatar ? (
                    <img src={banUser.avatar} alt={banUser.name} />
                  ) : (
                    <PersonIcon />
                  )}
                </div>
                <div className="ban-user-details">
                  <h4>{banUser.name}</h4>
                  <span>{banUser.email}</span>
                </div>
              </div>
              <textarea
                className="ban-textarea"
                placeholder="دلیل مسدود کردن کاربر را با جزئیات وارد کنید..."
                value={banReason}
                onChange={e => { setBanReason(e.target.value); if (e.target.value.trim()) setBanError(''); }}
              />
              {banError && <p className="ban-error">{banError}</p>}
            </>
          )}
        </DialogContent>
        <div className="ban-dialog-actions">
          <button className="btn btn-ghost" onClick={() => { setBanOpen(false); setBanUser(null); setBanReason(''); setBanError(''); }}>
            انصراف
          </button>
          <button className="btn btn-danger" onClick={handleBan} disabled={loading}>
            {loading ? <CircularProgress size={16} color="inherit" /> : <BlockIcon fontSize="small" />}
            مسدود کردن
          </button>
        </div>
      </Dialog>

      {/* Unban Dialog */}
      <Dialog open={unbanOpen} onClose={() => { setUnbanOpen(false); setUnbanUser(null); }} maxWidth="sm" fullWidth className="ban-dialog">
        <DialogTitle>رفع مسدودیت کاربر</DialogTitle>
        <DialogContent className="ban-dialog-content">
          {unbanUser && (
            <>
              <div className="ban-user-info">
                <div className="ban-user-avatar">
                  {unbanUser.avatar ? (
                    <img src={unbanUser.avatar} alt={unbanUser.name} />
                  ) : (
                    <PersonIcon />
                  )}
                </div>
                <div className="ban-user-details">
                  <h4>{unbanUser.name}</h4>
                  <span>{unbanUser.email}</span>
                </div>
              </div>
              {unbanUser.banReason && (
                <div className="ban-info-card" style={{ margin: 0 }}>
                  <div className="ban-info-row">
                    <span className="ban-info-label">دلیل مسدودیت:</span>
                    <span className="ban-info-value">{unbanUser.banReason}</span>
                  </div>
                </div>
              )}
              <p style={{ marginTop: 16, fontSize: 14 }}>آیا مطمئن هستید که می‌خواهید مسدودیت این کاربر را رفع کنید؟</p>
            </>
          )}
        </DialogContent>
        <div className="ban-dialog-actions">
          <button className="btn btn-ghost" onClick={() => { setUnbanOpen(false); setUnbanUser(null); }}>
            انصراف
          </button>
          <button className="btn btn-success" onClick={handleUnban} disabled={loading}>
            {loading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon fontSize="small" />}
            رفع مسدودیت
          </button>
        </div>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.type}>{snackbar.msg}</Alert>
      </Snackbar>
    </div>
  );
};

export default RegularUserManagement;
